import { topUpAmountProblem } from "@/modules/dashboard/wallet-top-ups/utils/walletTopUp";
import type { RuntimeWalletTransaction } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { compareDecimalStrings, subtractDecimals } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import {
  WALLET_DEBIT_CANCELLED_LEDGER_DESCRIPTION,
  WALLET_DEBIT_LEDGER_DESCRIPTION,
} from "../constants/walletDebits.constants";
import type { CreateWalletDebitPayload, WalletDebit } from "../types/walletDebits.types";

/**
 * A statement leg that is a Coloca debit for a manual send: the runtime posts it as `ADJUST` with this
 * description, and the `REVERSE` that gives it back when cancelled.
 */
export function isColocaDebit(
  transaction: Pick<RuntimeWalletTransaction, "operation_type" | "description">,
): boolean {
  const description = transaction.description?.trim();
  return (
    (transaction.operation_type === "ADJUST" && description === WALLET_DEBIT_LEDGER_DESCRIPTION) ||
    isCancelledColocaDebit(transaction)
  );
}

/** The leg that gives back a cancelled debit's amount: the reversal of its reserve. */
export function isCancelledColocaDebit(
  transaction: Pick<RuntimeWalletTransaction, "operation_type" | "description">,
): boolean {
  return (
    transaction.operation_type === "REVERSE" &&
    transaction.description?.trim() === WALLET_DEBIT_CANCELLED_LEDGER_DESCRIPTION
  );
}

/** A reserved debit older than this is flagged: the money is held and the send may have been forgotten. */
export const WALLET_DEBIT_STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export function isStaleReservation(
  debit: Pick<WalletDebit, "status" | "created_at">,
  now: number = Date.now(),
): boolean {
  return (
    debit.status === "RESERVED" && now - new Date(debit.created_at).getTime() > WALLET_DEBIT_STALE_AFTER_MS
  );
}

export type DebitAmountProblem = "invalid" | "not_positive" | "precision" | "exceeds";

/**
 * Mirrors the backend: a positive decimal string, at most as many decimals as the currency settles in, and at
 * most what the wallet has available (pending and blocked money cannot be debited).
 */
export function debitAmountProblem(
  amount: string,
  available: string,
  currency: string,
): DebitAmountProblem | null {
  const problem = topUpAmountProblem(amount, currency);
  if (problem) return problem;
  return compareDecimalStrings(amount.trim(), available) > 0 ? "exceeds" : null;
}

export function balanceAfterDebit(balance: string, amount: string): string {
  return subtractDecimals(balance, amount);
}

export function buildDebitPayload(input: CreateWalletDebitPayload): CreateWalletDebitPayload {
  const externalReference = input.external_reference?.trim();
  return {
    organization_id: input.organization_id,
    wallet_id: input.wallet_id,
    amount: input.amount.trim(),
    rail: input.rail ?? null,
    country: input.country ?? null,
    concept: input.concept.trim(),
    // Left out unless given: with it the backend records the send at once instead of holding the amount.
    ...(externalReference ? { external_reference: externalReference } : {}),
    internal_note: input.internal_note.trim(),
    idempotency_key: input.idempotency_key,
  };
}

/**
 * How a recorded debit's send is told: its rail (or null for one made some other way), what was sent by hand
 * and, with a rail, the fees the organization paid. Money as `<formatted> <currency>`.
 */
export function describeDebitSend(
  debit: Pick<WalletDebit, "amount" | "currency" | "rail" | "net_amount" | "commission" | "tax_total">,
  locale: string,
): { rail: string | null; sent: string; commission: string | null; taxes: string | null } {
  const money = (value: string) => `${formatCurrencyAmount(value, debit.currency, locale)} ${debit.currency}`;
  return {
    rail: debit.rail ? formatRail(debit.rail) : null,
    sent: money(debit.net_amount ?? debit.amount),
    commission: debit.rail ? money(debit.commission ?? "0") : null,
    taxes: debit.rail ? money(debit.tax_total ?? "0") : null,
  };
}

/** The form's choice for a send made some other way than a payout corridor: no fees. */
export const OTHER_CORRIDOR = "other";

/** A payout corridor as the form's radio value. */
export const corridorValue = (corridor: { rail: string; country: string }): string =>
  `${corridor.country}:${corridor.rail}`;

/** The corridor a radio value names; null for `OTHER_CORRIDOR` (or nothing chosen). */
export function corridorOf(value: string): { rail: string; country: string } | null {
  const [country, rail] = value.split(":");
  return country && rail ? { rail, country } : null;
}

/** "182 KB" / "2.4 MB": a file size for the proof list (bytes, not money, so plain numbers are fine). */
export function formatFileSize(bytes: number, locale: string): string {
  const format = (value: number, digits: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value);
  if (bytes < 1024 * 1024) return `${format(Math.max(1, Math.round(bytes / 1024)), 0)} KB`;
  return `${format(bytes / (1024 * 1024), 1)} MB`;
}
